import { useCallback, useState, useEffect } from "react";
import {
  FlowEvent,
  ChatEvent,
  LLMEvent,
  Message,
} from "@/shared/hooks/gpt/useGptFlow";
import { useGpt } from "@/shared/contexts/gptgateway.context";
import {
  useWisgptContextTokenQuery,
  WisgptContextTokenQueryVariables,
} from "@/shared/services/api/graphql/generated";
import { EVENT_NAMES, trackAppEvent } from "@/shared/utils/events/trackEvent";

// Type guard
const isMessage = (item: any): item is Message => item?.type === "message";

interface UseSummaryGenerationOptions {
  flowId: string;
  scope: WisgptContextTokenQueryVariables;
  onSaveSummary: (content: string) => void;
  onStartGeneration?: () => void;
  onEndGeneration?: () => void;
}

export function useSummaryGeneration({
  flowId,
  scope,
  onSaveSummary,
  onStartGeneration,
  onEndGeneration,
}: UseSummaryGenerationOptions) {
  const [summaryBuffer, setSummaryBuffer] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    setIsGenerating(false);
    return () => {
      // Cleanup on unmount
      setIsGenerating(false);
    };
  }, []);

  const { data } = useWisgptContextTokenQuery({
    variables: scope,
  });
  const signedScope = data?.wisgptContextToken || "";

  const { initializeFlow, runFlow, socket } = useGpt();

  useEffect(() => {
    return () => {
      // Cleanup listeners on unmount
      setSummaryBuffer("");
    };
  }, []);

  const summaryGenerationEventHandler = useCallback(
    (evt: FlowEvent, conversationId: string) => {
      if (evt.type === "nodeProcessStart") {
        setSummaryBuffer("");
        onStartGeneration?.();
      }

      if (evt.type === "chatEvent") {
        const llmEvent: LLMEvent = (evt as ChatEvent).chatEvent;
        if (llmEvent.event === "on_llm_start") {
          setSummaryBuffer("");
        } else if (llmEvent.event === "output_delta") {
          setSummaryBuffer((buf) => buf + (llmEvent.delta || ""));
        } else if (llmEvent.event === "full_output_block") {
          // Complete output block received
          if (llmEvent.item && isMessage(llmEvent.item)) {
            const content = llmEvent.item.content;
            setSummaryBuffer(content);
            // Save the content when we receive the full block
            onSaveSummary(content);
          }
        } else if (llmEvent.event === "on_llm_end") {
          // End of generation - cleanup
          setIsGenerating(false);
          onEndGeneration?.();
          // Remove the event listener after completion
          socket?.off(conversationId);
        }
      }
    },
    [onSaveSummary, onStartGeneration, onEndGeneration, socket]
  );

  const generateSummary = useCallback(async () => {
    if (isGenerating) return; // Prevent multiple simultaneous generations

    setSummaryBuffer("");
    setIsGenerating(true);
    onStartGeneration?.();

    try {
      const response = await initializeFlow(flowId, signedScope);
      if (response) {
        const eventHandler = (evt: FlowEvent) =>
          summaryGenerationEventHandler(evt, response.conversationId);

        socket?.on(response.conversationId, eventHandler);
        runFlow({
          runNodeId: response.runnableNodes[0],
          conversationId: response.conversationId,
        });

        trackAppEvent(EVENT_NAMES.GENERATE_GPT_SUMMARY, {
          flowId,
        });
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      setIsGenerating(false);
    }
  }, [
    flowId,
    signedScope,
    initializeFlow,
    socket,
    runFlow,
    summaryGenerationEventHandler,
    isGenerating,
    onStartGeneration,
  ]);

  return {
    generateSummary,
    summaryBuffer,
    isGenerating,
    setSummaryBuffer,
  };
}
